import { ImageSourcePropType } from 'react-native';
import { GO_IMAGE_CDN_URL, RAILS_CDN_IMAGE_PATH } from 'apps/rails/src/RailsConfig';

/**
 * ! IMPORTANT
 * Add images in webp format to RAILS-RNW/web/static/images/<folder>
 * - Use 'common' folder if the type of icon is commonly used, like an arrow icon.
 * - Else arrange them in feature-wise. Create a new folder (all in lower kebab case).
 * - Segregation and kebab case as a convention for the file name.
 * - Add the images into this file only if you are using them in code.
 * Not if the URL is coming from the server.
 */

const PATH = RAILS_CDN_IMAGE_PATH;
const WEBP = '.webp';
const TYPE = {
  COMMON: 'common/',
  TGS: 'tgs/',
  GO_ASSET: 'goAsset',
};

const urlGenerator = {
  get(iconsList: any, iconName: string) {
    if (iconsList.type) {
      return { uri: `${PATH}${iconsList.type}${iconsList[iconName]}${WEBP}` };
    }
    return iconsList[iconName];
  },
};

const goAssetUrlGenerator = {
  get(iconsList: any, iconName: string) {
    if (iconsList.type === TYPE.GO_ASSET) {
      return { uri: `${GO_IMAGE_CDN_URL}${iconsList[iconName]}` };
    }
  },
};

const ASSETS: Record<string, ImageSourcePropType> = {
  //Common Icons
  ...new Proxy(
    {
      type: TYPE.COMMON,
      greenOfferIcon: 'ic_offer_green',
      blueOfferIcon: 'ic_offer_blue',
    },
    urlGenerator,
  ),
  ...new Proxy(
    {
      type: TYPE.TGS,
      tgsBanner: 'tgs_banner',
    },
    urlGenerator,
  ),
  ...new Proxy(
    {
      type: TYPE.GO_ASSET,
      redirectionFailed: '1706027928926-redirection_failed.png',
      landingTgBannner: '1706031014252-tg_banner.webp',
      railsBookingCancelled: '1706027757748-rails_booking_cancelled.webp',
      myRailGpt: '1706027867824-myRailGPT.webp',
      tgIcon: '1706028888797-tg_icon.webp',
      tgTravellerBanner: '1706030312171-tg_traveller_banner.webp',
      lobIcons: '1706031150780-lob_icons.webp',
      tgsConfirmedBanner: '1706028123469-tgsConfirmedBanner.webp',
      tgsShare: '1706011081622-tgs_share.webp',
      whatsAppShare: '1706028982752-whatsApp_share.webp',
      tgsLandingIcon: '1706011360309-tgs_landing_icon.webp',
      tgBanner: '1706031014252-tg_banner.webp',
      tgPnrPageBanner: '1706028002281-tg_pnr_page_banner.webp',
      tgsTgBanner: '1706030977707-tgs_tg_banner.webp',
      refundBanner: '1706028394090-refund_banner.webp',
      useRefundBanner: '1706028683646-use_refund_banner.webp',
      upgradeBanner: '1706028341346-upgrade_banner.webp',
      testimonyComponent: '1706028297438-testimony_component.webp',
      testimony1: '1706028196590-testimony1.webp',
      testimony2: '1706028227608-testimony2.webp',
      testimony3: '1706028254976-testimony3.webp',
      confirmedOptionBolt: '1708666208825-confirmed_option_bolt.webp',
      icYellowTrain: '1708754350063-train_ic_yellow.webp',
      icTrainLTS: '1708921247390-ic_train_lts.webp',
      captchaNew: '1707900072785-captcha_new.webp',
      fcNotSelected: '1707900528517-fc_not_selected.webp',
      fcSelected: '1707900814886-fc_selected.webp',
      maintenanceError: '1707901255799-maintenance_error.jpeg',
      pnrLoaderImg: '1707901416644-pnr_loader_img.webp',
      icLogin: '1707901614951-ic_login.webp',
      icRefundBank: '1707902099413-ic_refund_bank.webp',
      icRefundWallet: '1707902187038-ic_refund_wallet.webp',
      icTimeout: '1707902320670-ic_timeout.webp',
      icFpTick: '1707903047223-ic_fp_tick.webp',
      icGenericError: '1707903559090-ic_generic_error.webp',
      rectangleCopy: '1707903771350-rectangle_copy.webp',
      railsNewTag: '1713885737161-rails_new_tag.webp',
      warningExclamationImage: '1713434088536-warning_image.png',
      availabilitySubscriptionIcon: '1718695787183-AvailabilitySubscriptionIcon.png',
      blueAvailabilitySubscriptionIcon: '1718795740645-blueAvailabilitySubscriptionIcon.png',
      greenTick: '1718795810799-greentick.png',
      seatLockGreyIcon:'1711437903515-Seat%20Lock%20New%20Logo.png',
      electricBolt: '1718961284218-electricBolt.webp',
      dynamicPriceIcon: '1723451693422-dynamicPriceIcon.webp',
      irctcUserCard: '1726221858268-IrctcUserCreation.webp',
      progressBar: '1726223007974-ProgressBar.webp',
      scheduleTatakalRemainder: '1726131241820-schedule_tatkal_remainder.webp',
      scheduleTatkalBookingOpen: '1726131371212-schedule_tatkal_booking_open.webp',
      offersIcon: '1728103879216-offers_Icon.webp',
      tickIcon: '1728104440904-tick_icon.webp',
      greenTick2: '1728104813071-green_tick.webp',
      railsFoodOnTrainsBanner: '1749201762938-foodOnTrainsBanner.webp',
      foodInTrain: '1748785609517-foodInTrain.webp',
      foodIcon: '1748853111811-mealsThali.webp',
      foodVector: '1748788239237-food2ndVector.png',
      mealsVector: '1748786164503-mealsVector.webp',
      closeIcon: '1750143518286-close_icon.png',
    },
    goAssetUrlGenerator,
  ),
  //Place static icons here just like example below
  ...{
    ic_arrow_right: require('@mmt/legacy-assets/src/ic_arrow_right.webp'),
    ic_arrow_right_small: require('@mmt/legacy-assets/src/Arrow.webp'),
    ic_calendar: require('@mmt/legacy-assets/src/calendar.webp'),
    ic_offer: require('@mmt/legacy-assets/src/TGS/coupon.webp'),
    ic_copy: require('@mmt/legacy-assets/src/pnrStatusPageCopyButton.webp'),
    ic_arrow_blue_filled: require('@mmt/legacy-assets/src/ArrowBlueFilled.webp'),
    ic_train_schedule: require('@mmt/legacy-assets/src/ic_raillanding_trainschedule.webp'),
    ic_close: require('@mmt/legacy-assets/src/close-black.webp'),
    ic_star_white: require('@mmt/legacy-assets/src/star_icon.webp'),
    ic_whatsapp: require('@mmt/legacy-assets/src/ic_whatsapp.webp'),
    trainAndBusIcon: require('@mmt/legacy-assets/src/rails_ct_trains&bus.webp'),
    crossIcon: require('@mmt/legacy-assets/src/grey_cross.webp'),
    downArrow: require('@mmt/legacy-assets/src/arrow_downGrey.webp'),
    busIcon: require('@mmt/legacy-assets/src/connected_travel_bus_icon.webp'),
    trainIcon: require('@mmt/legacy-assets/src/connected_travel_train_icon.webp'),
    ctClockIcon: require('@mmt/legacy-assets/src/rails/ctClockIcon.webp'),
    ctLocationIcon: require('@mmt/legacy-assets/src/rails/ctLocationIcon.webp'),
    whiteCrossIcon: require('@mmt/legacy-assets/src/rails/white_cross.webp'),
    checkBoxActive: require('@mmt/legacy-assets/src/ic-check-box-active.webp'),
    checkBoxInactive: require('@mmt/legacy-assets/src/ic-check-box-inactive.webp'),
    greenTick: require('@mmt/legacy-assets/src/rails/green_tick.webp'),
    updatesIcon: require('@mmt/legacy-assets/src/updates_icon.webp'),
    errorInfoIcon: require('@mmt/legacy-assets/src/error_filled.webp'),
    redTick: require('@mmt/legacy-assets/src/red_tick.webp'),
    cancelIcon: require('@mmt/legacy-assets/src/rails/Cancel_icon.png'),
  }
};

export default ASSETS;
